import React from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Divider,
} from '@material-ui/core';

interface FullReportPDFProps {
  data: any[];
  isRTL: boolean;
  words: any;
  isPrint?: boolean;
}

export const FullReportPDF: React.FC<FullReportPDFProps> = ({
  data,
  isRTL,
  words,
  isPrint = false,
}) => {
  const totalIncome = data.reduce(
    (sum, item) => sum + (item.totalincome || 0),
    0,
  );
  const totalDepreciation = data.reduce(
    (sum, item) => sum + (item.totaldepreciation || 0),
    0,
  );
  const totalQty = data.reduce((sum, item) => sum + (item.totalqty || 0), 0);

  const containerStyle = isPrint
    ? {
        width: '297mm',
        minHeight: '210mm',
        backgroundColor: 'white',
        padding: '20px',
        fontFamily: isRTL ? 'Tahoma, Arial, sans-serif' : 'inherit',
      }
    : {
        width: '297mm',
        minHeight: '210mm',
        backgroundColor: 'white',
        fontFamily: isRTL
          ? '"Tahoma", "Arial", "DejaVu Sans", sans-serif'
          : 'inherit',
      };

  return (
    <Box p={3} style={containerStyle} className={isPrint ? 'print-break' : ''}>
      {/* Font loading for Arabic text */}
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&display=swap');
          @import url('https://fonts.googleapis.com/css2?family=Lateef:wght@400;700&display=swap');
          .arabic-text {
            font-family: 'Amiri', 'Lateef', 'Tahoma', 'Arial', sans-serif !important;
            line-height: 1.6;
          }
        `}
      </style>

      {/* Header */}
      <Typography
        variant="h4"
        align="center"
        gutterBottom
        className="arabic-text"
        style={{
          fontWeight: 'bold',
          fontSize: '2rem',
        }}
      >
        {isRTL ? 'تقرير البنود المؤجرة' : 'Rent Items Report'}
      </Typography>
      <Typography
        variant="subtitle1"
        align="center"
        color="textSecondary"
        gutterBottom
        className="arabic-text"
      >
        {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
      </Typography>
      <Divider style={{ marginBottom: '20px' }} />

      {/* Summary */}
      <Box mb={3}>
        <Typography
          variant="h6"
          gutterBottom
          className="arabic-text"
          style={{ fontSize: '1.25rem' }}
        >
          {isRTL ? 'ملخص التقرير' : 'Report Summary'}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell style={{ width: '25%' }}>
                  <strong>{isRTL ? 'عدد البنود' : 'Total Items'}</strong>
                </TableCell>
                <TableCell style={{ width: '15%' }} align="right">
                  {data.length}
                </TableCell>
                <TableCell style={{ width: '25%' }}>
                  <strong>
                    {isRTL ? 'إجمالي مرات التأجير' : 'Total Rent Count'}
                  </strong>
                </TableCell>
                <TableCell style={{ width: '35%' }} align="right">
                  {totalQty}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <strong>{isRTL ? 'إجمالي الدخل' : 'Total Income'}</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{totalIncome.toFixed(2)}</strong>
                </TableCell>
                <TableCell>
                  <strong>
                    {isRTL ? 'إجمالي الاهتلاك' : 'Total Depreciation'}
                  </strong>
                </TableCell>
                <TableCell align="right">
                  <strong>{totalDepreciation.toFixed(2)}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Detailed Table */}
      <Typography
        variant="h6"
        gutterBottom
        className="arabic-text"
        style={{ fontSize: '1.25rem' }}
      >
        {isRTL ? 'تفاصيل البنود' : 'Items Details'}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
              <TableCell
                className="arabic-text"
                style={{
                  width: '25%',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
              >
                {isRTL ? 'اسم البند' : 'Item Name'}
              </TableCell>
              <TableCell
                className="arabic-text"
                style={{
                  width: '10%',
                  fontSize: '13px',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                {isRTL ? 'تكلفة التأجير' : 'Rent Cost'}
              </TableCell>
              <TableCell
                className="arabic-text"
                style={{
                  width: '8%',
                  fontSize: '13px',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                {isRTL ? 'عدد التأجيرات' : 'Rent Count'}
              </TableCell>
              <TableCell
                className="arabic-text"
                style={{
                  width: '10%',
                  fontSize: '13px',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                {isRTL ? 'اهتلاك سنوي' : 'Yearly Depreciation'}
              </TableCell>
              <TableCell
                className="arabic-text"
                style={{
                  width: '10%',
                  fontSize: '13px',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                <strong>
                  {isRTL ? 'اهتلاك التأجير' : 'Rent Depreciation'}
                </strong>
              </TableCell>
              <TableCell
                className="arabic-text"
                style={{
                  width: '12%',
                  fontSize: '13px',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                {isRTL ? 'الدخل' : 'Income'}
              </TableCell>
              <TableCell
                className="arabic-text"
                style={{
                  width: '12%',
                  fontSize: '13px',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                <strong>
                  {isRTL ? 'مجموع الاهتلاك' : 'Total Depreciation'}
                </strong>
              </TableCell>
              <TableCell
                className="arabic-text"
                style={{
                  width: '13%',
                  fontSize: '13px',
                  fontWeight: 'bold',
                }}
                align="right"
              >
                {isRTL ? 'المتبقي' : 'Remaining'}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => (
              <TableRow
                key={item._id}
                style={{
                  backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                }}
              >
                <TableCell
                  className="arabic-text"
                  style={{
                    padding: '12px 8px',
                    fontSize: '14px',
                  }}
                >
                  {item.itemName || '-'}
                </TableCell>
                <TableCell
                  style={{
                    padding: '12px 8px',
                    fontSize: '13px',
                  }}
                  align="right"
                >
                  {(item.rentitemcost || 0).toFixed(2)}
                </TableCell>
                <TableCell
                  style={{
                    padding: '12px 8px',
                    fontSize: '13px',
                  }}
                  align="right"
                >
                  {item.totalqty || 0}
                </TableCell>
                <TableCell
                  style={{
                    padding: '12px 8px',
                    fontSize: '13px',
                  }}
                  align="right"
                >
                  {(item.timedepreciation || 0).toFixed(2)}
                </TableCell>
                <TableCell
                  style={{
                    padding: '12px 8px',
                    fontSize: '13px',
                  }}
                  align="right"
                >
                  {(item.rentdepreciation || 0).toFixed(2)}
                </TableCell>
                <TableCell
                  style={{
                    padding: '12px 8px',
                    fontSize: '13px',
                  }}
                  align="right"
                >
                  <strong>{(item.totalincome || 0).toFixed(2)}</strong>
                </TableCell>
                <TableCell
                  style={{
                    padding: '12px 8px',
                    fontSize: '13px',
                  }}
                  align="right"
                >
                  {(item.totaldepreciation || 0).toFixed(2)}
                </TableCell>
                <TableCell
                  style={{
                    padding: '12px 8px',
                    fontSize: '13px',
                  }}
                  align="right"
                >
                  {(item.remaining || 0).toFixed(2)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer */}
      <Box mt={3} pt={2} borderTop="1px solid #ddd">
        <Typography
          variant="caption"
          color="textSecondary"
          align="center"
          display="block"
          className="arabic-text"
        >
          {isRTL
            ? 'تم إنشاء هذا التقرير بواسطة نظام إدارة الجدولة'
            : 'Generated by Schedule Management System'}
        </Typography>
      </Box>
    </Box>
  );
};

interface IndividualReportPDFProps {
  item: any;
  isRTL: boolean;
  words: any;
}

export const IndividualReportPDF: React.FC<IndividualReportPDFProps> = ({
  item,
  isRTL,
  words,
}) => {
  return (
    <Box
      p={4}
      style={{ width: '210mm', minHeight: '297mm', backgroundColor: 'white' }}
    >
      {/* Header */}
      <Typography
        variant="h4"
        align="center"
        gutterBottom
        style={{ fontFamily: isRTL ? 'Tahoma, Arial, sans-serif' : 'inherit' }}
      >
        {isRTL ? 'تقرير بند مؤجر فردي' : 'Individual Rent Item Report'}
      </Typography>
      <Typography
        variant="h5"
        align="center"
        color="primary"
        gutterBottom
        style={{ fontFamily: isRTL ? 'Tahoma, Arial, sans-serif' : 'inherit' }}
      >
        {item.itemName || 'N/A'}
      </Typography>
      <Typography
        variant="subtitle1"
        align="center"
        color="textSecondary"
        gutterBottom
        style={{ fontFamily: isRTL ? 'Tahoma, Arial, sans-serif' : 'inherit' }}
      >
        {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
      </Typography>
      <Divider style={{ marginBottom: '30px' }} />

      {/* Item Details */}
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          {isRTL ? 'معلومات البند' : 'Item Information'}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="medium">
            <TableBody>
              <TableRow>
                <TableCell style={{ width: '40%', padding: '16px' }}>
                  <strong>{isRTL ? 'اسم البند' : 'Item Name'}</strong>
                </TableCell>
                <TableCell
                  style={{ width: '60%', padding: '16px' }}
                  align="right"
                >
                  {item.itemName || '-'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ padding: '16px' }}>
                  <strong>{isRTL ? 'تكلفة التأجير' : 'Rent Item Cost'}</strong>
                </TableCell>
                <TableCell style={{ padding: '16px' }} align="right">
                  {(item.rentitemcost || 0).toFixed(2)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ padding: '16px' }}>
                  <strong>
                    {isRTL ? 'عدد مرات التأجير' : 'Total Rent Count'}
                  </strong>
                </TableCell>
                <TableCell style={{ padding: '16px' }} align="right">
                  {item.totalqty || 0}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ padding: '16px' }}>
                  <strong>
                    {isRTL ? 'السعر لكل تأجير' : 'Price Per Rent'}
                  </strong>
                </TableCell>
                <TableCell style={{ padding: '16px' }} align="right">
                  {((item.totalincome || 0) / (item.totalqty || 1)).toFixed(2)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Financial Analysis */}
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          {isRTL ? 'التحليل المالي' : 'Financial Analysis'}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="medium">
            <TableBody>
              <TableRow>
                <TableCell style={{ width: '40%', padding: '16px' }}>
                  <strong>
                    {isRTL ? 'اهتلاك سنوي' : 'Yearly Depreciation'}
                  </strong>
                </TableCell>
                <TableCell
                  style={{ width: '60%', padding: '16px' }}
                  align="right"
                >
                  {(item.timedepreciation || 0).toFixed(2)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ padding: '16px' }}>
                  <strong>
                    {isRTL ? 'اهتلاك التأجير' : 'Rent Depreciation'}
                  </strong>
                </TableCell>
                <TableCell style={{ padding: '16px' }} align="right">
                  {(item.rentdepreciation || 0).toFixed(2)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ padding: '16px' }}>
                  <strong>
                    {isRTL ? 'مجموع الاهتلاك' : 'Total Depreciation'}
                  </strong>
                </TableCell>
                <TableCell style={{ padding: '16px' }} align="right">
                  <strong>{(item.totaldepreciation || 0).toFixed(2)}</strong>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ padding: '16px' }}>
                  <strong>
                    {isRTL ? 'القيمة المتبقية' : 'Remaining Value'}
                  </strong>
                </TableCell>
                <TableCell style={{ padding: '16px' }} align="right">
                  <strong>{(item.remaining || 0).toFixed(2)}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Revenue Summary */}
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          {isRTL ? 'ملخص الإيرادات' : 'Revenue Summary'}
        </Typography>
        <Paper
          variant="outlined"
          style={{
            padding: '40px',
            backgroundColor: '#f8f9fa',
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" color="primary" gutterBottom>
            {isRTL ? 'إجمالي الدخل' : 'Total Income'}
          </Typography>
          <Typography
            variant="h2"
            color="primary"
            style={{ fontWeight: 'bold', fontSize: '3rem' }}
          >
            {(item.totalincome || 0).toFixed(2)}
          </Typography>
        </Paper>
      </Box>

      {/* Performance Metrics */}
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          {isRTL ? 'مؤشرات الأداء' : 'Performance Metrics'}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="medium">
            <TableBody>
              <TableRow>
                <TableCell style={{ width: '40%', padding: '16px' }}>
                  <strong>
                    {isRTL ? 'نسبة العائد على التكلفة' : 'Return on Cost Ratio'}
                  </strong>
                </TableCell>
                <TableCell
                  style={{ width: '60%', padding: '16px' }}
                  align="right"
                >
                  {item.rentitemcost
                    ? ((item.totalincome / item.rentitemcost) * 100).toFixed(2)
                    : '0'}
                  %
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ padding: '16px' }}>
                  <strong>
                    {isRTL
                      ? 'متوسط الدخل لكل تأجير'
                      : 'Average Income Per Rent'}
                  </strong>
                </TableCell>
                <TableCell style={{ padding: '16px' }} align="right">
                  {item.totalqty
                    ? (item.totalincome / item.totalqty).toFixed(2)
                    : '0'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ padding: '16px' }}>
                  <strong>
                    {isRTL ? 'نسبة الاهتلاك' : 'Depreciation Ratio'}
                  </strong>
                </TableCell>
                <TableCell style={{ padding: '16px' }} align="right">
                  {item.totalincome
                    ? (
                        ((item.totaldepreciation || 0) / item.totalincome) *
                        100
                      ).toFixed(2)
                    : '0'}
                  %
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Footer */}
      <Box mt={4} pt={3} borderTop="1px solid #ddd">
        <Typography
          variant="body1"
          color="textSecondary"
          align="center"
          display="block"
          gutterBottom
        >
          {isRTL
            ? 'تم إنشاء هذا التقرير بواسطة نظام إدارة الجدولة'
            : 'Generated by Schedule Management System'}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          align="center"
          display="block"
        >
          {isRTL ? 'معرف البند' : 'Item ID'}: {item._id}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          align="center"
          display="block"
        >
          {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}{' '}
          {new Date().toLocaleTimeString(isRTL ? 'ar-SA' : 'en-US')}
        </Typography>
      </Box>
    </Box>
  );
};
